import { useEffect, useState } from "react";
import { useAuth, useUser } from "@clerk/react";
import { Save, User2Icon } from "lucide-react";
import { toast } from "sonner";
import { useUserSettingsStore } from "@/stores";
import { useAdminRole } from "@/hooks/IsAdminContext";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Frame,
  FrameHeader,
  FrameTitle,
  FrameDescription,
  FramePanel,
  FrameFooter,
  FrameAction,
} from "@/components/ui/frame";
import {
  MessageVariables,
  MessageVariable,
} from "@/components/dashboard_campaign/brief/MessageVariables";
import { ScopeWrapper } from "@/components/ui/scope-wrapper";

const WRITING_STYLE_VARIABLES: MessageVariable[] = [
  { variable: "creator_fullname", description: "Creator's full name" },
  { variable: "creator_username", description: "Creator's username" },
  {
    variable: "creator_summary",
    description: "AI summary of creator's content",
  },
  { variable: "user_name", description: "Your name (sender)" },
  {
    variable: "organization_description",
    description: "Organization description",
  },
  { variable: "organization_name", description: "Your organization name" },
  { variable: "organization_website", description: "Organization website URL" },
  { variable: "campaign_name", description: "Campaign name" },
  {
    variable: "campaign_platform",
    description: "Platform (TikTok, Instagram, YouTube)",
  },
  {
    variable: "partnership",
    description: "Partnership details (paid, gifting, amount, etc.)",
  },
];

/**
 * Amy's per-user writing style (users.user_writing_style). Everyone edits
 * their own; admins pick any member from the header.
 */
export function WritingStyleFrame() {
  const {
    userInfo,
    saveUserSettingFor,
    fetchUserWritingStyle,
    fetchOrganizationUsers,
    organizationUsers,
    isLoading,
  } = useUserSettingsStore();
  const { orgId, getToken } = useAuth();
  const { user } = useUser();
  const { isAdmin } = useAdminRole();

  const [selectedUserId, setSelectedUserId] = useState<string>(user?.id || "");
  const isSelf = selectedUserId === user?.id;

  const [writingStyle, setWritingStyle] = useState("");
  const [initialWritingStyle, setInitialWritingStyle] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingStyle, setLoadingStyle] = useState(false);

  // Keep selection pinned to the logged-in user once Clerk resolves it.
  useEffect(() => {
    if (user?.id && !selectedUserId) setSelectedUserId(user.id);
  }, [user?.id, selectedUserId]);

  // Load the org member list for the admin selector.
  useEffect(() => {
    if (!isAdmin || !orgId) return;
    (async () => {
      const token = await getToken();
      if (token) fetchOrganizationUsers(orgId, token);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, orgId]);

  // Seed the textarea from whichever user is selected.
  useEffect(() => {
    if (!selectedUserId) return;

    if (isSelf) {
      const style = userInfo?.user_writing_style || "";
      setWritingStyle(style);
      setInitialWritingStyle(style);
      return;
    }

    if (!orgId) return;
    let cancelled = false;
    (async () => {
      setLoadingStyle(true);
      const token = await getToken();
      if (!token) {
        setLoadingStyle(false);
        return;
      }
      const data = await fetchUserWritingStyle(orgId, selectedUserId, token);
      if (cancelled) return;
      const style = data?.user_writing_style || "";
      setWritingStyle(style);
      setInitialWritingStyle(style);
      setLoadingStyle(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId, isSelf, userInfo, orgId]);

  const hasChanges = writingStyle !== initialWritingStyle;

  const handleSaveWritingStyle = async () => {
    if (!orgId || !selectedUserId) return;
    setSaving(true);
    try {
      const token = await getToken();
      if (!token) return;
      const success = await saveUserSettingFor(
        selectedUserId,
        orgId,
        "user_writing_style",
        writingStyle,
        token,
        { mirrorToGlobal: isSelf },
      );
      if (success) {
        setInitialWritingStyle(writingStyle);
        toast.success("Writing Style saved successfully");
      } else {
        toast.error("Failed to save Writing Style");
      }
    } catch {
      toast.error("Failed to save Writing Style");
    } finally {
      setSaving(false);
    }
  };

  // Build the admin selector options, ensuring the logged-in user is present
  // and listed first even before the org members list resolves.
  const selfFallback = {
    clerk_user_id: user?.id || "",
    name: user?.fullName || user?.primaryEmailAddress?.emailAddress || "You",
    email: user?.primaryEmailAddress?.emailAddress || "",
    imageUrl: user?.imageUrl || "",
  };
  const userOptions = organizationUsers.some((u) => u.clerk_user_id === user?.id)
    ? [...organizationUsers].sort((a) => (a.clerk_user_id === user?.id ? -1 : 0))
    : [selfFallback, ...organizationUsers];
  const selectedUser =
    userOptions.find((u) => u.clerk_user_id === selectedUserId) || selfFallback;

  return (
    <Frame>
      <FrameHeader>
        <FrameTitle>Writing Style</FrameTitle>
        <FrameDescription>The tone Amy writes in for each member.</FrameDescription>
        <FrameAction>
          {isAdmin && (
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <ScopeWrapper>
                <SelectTrigger variant="ghost" className="h-auto py-1.5 pl-1.5">
                  <SelectValue placeholder="Select user">
                    <MemberOption member={selectedUser} isSelf={selectedUser.clerk_user_id === user?.id} />
                  </SelectValue>
                </SelectTrigger>
              </ScopeWrapper>
              <SelectContent align="end" className="min-w-62">
                {userOptions.map((u) => (
                  <SelectItem key={u.clerk_user_id} value={u.clerk_user_id}>
                    <MemberOption member={u} isSelf={u.clerk_user_id === user?.id} />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </FrameAction>
      </FrameHeader>
      {(isLoading || loadingStyle) && !writingStyle ? (
        <FramePanel>
          <Skeleton className="h-[266px]" />
        </FramePanel>
      ) : (
        <Textarea
          variant="panel"
          className="min-h-[300px]"
          placeholder="Enter your writing style examples here..."
          value={writingStyle}
          onChange={(e) => setWritingStyle(e.target.value)}
          onBlur={handleSaveWritingStyle}
        />
      )}
      {/* The variables sit under the editor, not in a second box. */}
      <FrameFooter className="flex items-start justify-between gap-3">
        <MessageVariables variables={WRITING_STYLE_VARIABLES} />
        {hasChanges && (
          <Button
            onClick={handleSaveWritingStyle}
            disabled={saving}
            size="sm"
            className="animate-in fade-in-0 duration-200"
          >
            {saving ? <Spinner /> : <Save />}
            Save
          </Button>
        )}
      </FrameFooter>
    </Frame>
  );
}

/** Avatar + name (+ "You") + email — the picker's trigger and its rows. */
function MemberOption({
  member,
  isSelf,
}: {
  member: { name: string; email: string; imageUrl: string };
  isSelf: boolean;
}) {
  return (
    <div className="flex items-center gap-2 text-left">
      <Avatar variant="square">
        <AvatarImage src={member.imageUrl} alt={member.name} />
        <AvatarFallback>
          <User2Icon className="size-4" />
        </AvatarFallback>
      </Avatar>
      <div className="grid flex-1 leading-tight">
        <span className="flex items-center gap-1.5 truncate text-sm font-medium">
          {member.name}
          {isSelf && <Badge variant="flat">You</Badge>}
        </span>
        {member.email && (
          <span className="truncate text-xs text-muted-foreground">{member.email}</span>
        )}
      </div>
    </div>
  );
}
